import { Button, Collapse, Empty, Modal, Popover } from "antd";
import React, { useEffect, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import "./CandidateDetailModal.scss";
import linkedImg from "@assets/img/logo/Linkedin-color.svg";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import RadarChart from "@components/RadarChart/RadarChart";
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  MinusCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import Checkbox from "antd/es/checkbox/Checkbox";

interface TCandidateDetailModal {
  isOpen: any;
  cancel: () => void;
  candidateProfile: any;
  candidateEvaluate: any;
  selectedDetail: string;
  setSelectedDetail: (value: string) => void;
  selectedList: any[];
  setSelectedList: (list: any) => void;
  notSelectedList: any[];
  setNotSelectedList: (list: any) => void;
  excludeList: any[];
  setExcludeList: (list: any) => void
}

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const CandidateDetailModal: React.FC<TCandidateDetailModal> = ({
  isOpen,
  cancel,
  candidateProfile,
  candidateEvaluate,
  setSelectedDetail,
  selectedDetail,
  selectedList,
  setSelectedList,
  notSelectedList,
  setNotSelectedList,
  excludeList,
  setExcludeList
}) => {
  const [datasets, setDatasets] = useState([0, 0, 0, 0, 0]);
  const [selectedProfile, setSelectedProfile] = useState<any>("");
  const [selectedEvaluate, setSelectedEvaluate] = useState<any>("");

  const [feedback, setFeedback] = useState<any>();

  // LISTER CANDIDATE PROFILE CHANGE THEN UPDATING THE NOT SELECTED LIST
  useEffect(() => {
    setNotSelectedList([...candidateProfile]);
  }, [candidateProfile]);

  // HANDLE ADD/REMOVE SELECTED ITEM
  const handleSelectedItem = (
    selectedItem: any,
    checked: boolean,
    from?: string
  ) => {
    if (checked) {
      if (from == "not selected") {
        const updatedArray = notSelectedList.filter(
          (item: any) => item.slug !== selectedItem.slug
        );
        setNotSelectedList(updatedArray);
        setSelectedList([...selectedList, selectedItem]);
      } else {
        const updatedArray = excludeList.filter(
          (item: any) => item.slug !== selectedItem.slug
        );
        setExcludeList(updatedArray);
        setSelectedList([...selectedList, selectedItem]);
      }
    }
  };

  // HANDLE ADD/REMOVE EXCLUDED ITEM
  const handleExcludedItem = (excludedItem: any, from: string) => {
    if (from == "not selected") {
      const updatedArray = notSelectedList.filter(
        (item: any) => item.slug !== excludedItem.slug
      );
      setNotSelectedList(updatedArray);
      setExcludeList([...excludeList, excludedItem]);
    } else {
      const updatedArray = selectedList.filter(
        (item: any) => item.slug !== excludedItem.slug
      );
      setSelectedList(updatedArray);
      setExcludeList([...excludeList, excludedItem]);
    }
  };

  // CALCULATE TOTAL CONFIDENCE
  const calculateConfidence = () => {
    if (feedback !== undefined) {
      const exp = parseFloat(feedback?.confidence?.experience);
      const edu = parseFloat(feedback?.confidence?.education);
      const cert = parseFloat(feedback?.confidence?.certifications);
      const lang = parseFloat(feedback?.confidence?.languages);
      const skill = parseFloat(feedback?.confidence?.skills);

      const total = (exp + edu + cert + lang + skill) * 100;
      return Math.floor(total);
    }
    return 0;
  };

  // COLLAPSE CONFIG
  const collapseConfig = [
    {
      key: 0,
      label: `Not selected (${notSelectedList.length}/${candidateProfile.length})`,
      children: (
        <div className="candidate-list">
          {notSelectedList.map((item: any, index: number) => {
            var filterEvalute: any = candidateEvaluate.filter(
              (ite: any) => ite.slug === item.slug
            );
            return (
              <ProfileItem
                key={index}
                item={item}
                index={index}
                selectedDetail={selectedDetail}
                setSelectedDetail={setSelectedDetail}
                score={filterEvalute}
                handleSelectedItem={handleSelectedItem}
                handleExcludedItem={handleExcludedItem}
                positionList={"not selected"}
              />
            );
          })}
        </div>
      ),
    },
    {
      key: 1,
      label: `Selected (${selectedList.length}/${candidateProfile.length})`,
      children: (
        <div className="candidate-list">
          {selectedList.map((item: any, index: number) => {
            var filterEvalute: any = candidateEvaluate.filter(
              (ite: any) => ite.slug === item.slug
            );
            return (
              <ProfileItem
                key={index}
                item={item}
                index={index}
                selectedDetail={selectedDetail}
                setSelectedDetail={setSelectedDetail}
                score={filterEvalute}
                handleSelectedItem={handleSelectedItem}
                handleExcludedItem={handleExcludedItem}
                positionList={"selected"}
              />
            );
          })}
        </div>
      ),
    },
    {
      key: 2,
      label: `Excluded (${excludeList.length}/${candidateProfile.length})`,
      children: (
        <div className="candidate-list">
          {excludeList.map((item: any, index: number) => {
            var filterEvalute: any = candidateEvaluate.filter(
              (ite: any) => ite.slug === item.slug
            );
            return (
              <ProfileItem
                key={index}
                item={item}
                index={index}
                selectedDetail={selectedDetail}
                setSelectedDetail={setSelectedDetail}
                score={filterEvalute}
                handleSelectedItem={handleSelectedItem}
                handleExcludedItem={handleExcludedItem}
                positionList={"excluded"}
              />
            );
          })}
        </div>
      ),
    },
  ];

  // LISTEN CANDIDATE SELECTION FOR DISPLAY INFO - CHART - EVALUATE
  useEffect(() => {
    if (selectedDetail !== "") {
      candidateEvaluate.forEach((item: any) => {
        if (item.slug === selectedDetail) {
          const newDatasets = [
            item.overall,
            item.skills,
            item.education,
            item.languages,
            item.experience,
          ];
          setDatasets(newDatasets);
        }
      });
      const newSelectedProfile = candidateProfile.find(
        (item: any) => item.slug === selectedDetail
      );
      const newSelectedEvaluate = candidateEvaluate.find(
        (item: any) => item.slug === selectedDetail
      );
      setSelectedProfile(newSelectedProfile);
      setSelectedEvaluate(newSelectedEvaluate);
    }
  }, [selectedDetail, candidateEvaluate, candidateProfile]);

  // LISTEN CANDIDATE SELECTION FOR DISPLAY FEEDBACK
  useEffect(() => {
    if (selectedEvaluate !== "") {
      const extractedFeedback =
        selectedEvaluate !== undefined
          ? JSON.parse(selectedEvaluate?.feed_back)
          : {};
      setFeedback(extractedFeedback);
    }
  }, [selectedEvaluate]);

  return (
    <Modal
      title={"Candidate Detail"}
      className="CandidateDetail"
      open={isOpen}
      onCancel={cancel}
      width={"90vw"}
      footer={false}
    >
      <div className="row">
        <div className="col-xl-3 col-lg-4">
          <div className="collapse-header-dash">
            <Popover
              content={<PopoverContentState />}
              placement="rightTop"
              title={`What is "state"?`}
              trigger="click"
              overlayClassName="Candidate-Popover-State"
            >
              <InfoCircleOutlined />
            </Popover>
            <div className="candidate-label">Candidates</div>
            <div className="score-label">Score</div>
          </div>
          <Collapse
            defaultActiveKey={[0, 1, 2]}
            items={collapseConfig}
            onChange={() => console.log(1)}
          />
        </div>
        <div className="col-xl-5 col-lg-4">
          <div className="candidate-profile">
            <div className="profile-avatar-group w-100">
              <div className="profile-avatar">
                <img
                  className="rounded-circle"
                  src={selectedProfile?.details?.avatar}
                  width="150"
                  alt={selectedProfile?.details?.name}
                />
                <div className="profile-total-score">
                  {parseInt(selectedEvaluate?.overall) / 10}/10
                </div>
              </div>
            </div>
            <div className="profile-name text-center mt-2">
              {selectedProfile?.details?.name}
            </div>
            <div className="profile-position text-center">
              {selectedProfile?.details?.postion}
            </div>

            <div className="profile-detail">
              <div className="profile-introduce mt-3">
                <div className="profile-title">About</div>
                {selectedProfile?.details?.crawl_profile_about === null ||
                selectedProfile?.details?.crawl_profile_about === undefined ? (
                  <div className="d-flex w-100 align-items-center justify-content-center">
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  </div>
                ) : (
                  selectedProfile?.details?.crawl_profile_about[0].about
                )}
              </div>
              <div className="profile-experience mt-3">
                <div className="profile-title">Work Experiences</div>
                {selectedProfile?.details?.crawl_profile_exps?.length > 0 ? (
                  selectedProfile?.details?.crawl_profile_exps.map(
                    (item: any, index: any) => {
                      return (
                        <div className="exp-item" key={index}>
                          {item.time_period}: {item.job_title} at{" "}
                          {item.company_name}
                        </div>
                      );
                    }
                  )
                ) : (
                  <div className="d-flex w-100 align-items-center justify-content-center">
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  </div>
                )}
              </div>
              <div className="profile-education mt-3">
                <div className="profile-title">Education</div>
                {selectedProfile?.details?.crawl_profile_educations?.length >
                0 ? (
                  selectedProfile?.details?.crawl_profile_educations.map(
                    (item: any, index: any) => {
                      return (
                        <div className="edu-item" key={index}>
                          {item.time_period}: {item.degree_type} in{" "}
                          {item.school_name}
                        </div>
                      );
                    }
                  )
                ) : (
                  <div className="d-flex w-100 align-items-center justify-content-center">
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  </div>
                )}
              </div>
              <div className="profile-skills mt-3">
                <div className="profile-title">Skills</div>
                <div className="profile-skills-wrap d-flex flex-wrap gap-1">
                  {selectedProfile?.details?.crawl_profile_skills?.length >
                  0 ? (
                    selectedProfile?.details?.crawl_profile_skills.map(
                      (item: any, index: any) => {
                        return (
                          <div className="skills-item" key={index}>
                            {item.skill_name}
                          </div>
                        );
                      }
                    )
                  ) : (
                    <div className="d-flex w-100 align-items-center justify-content-center">
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                  )}
                </div>
              </div>
              <div className="profile-languages mt-3">
                <div className="profile-title">Languages</div>
                <div className="profile-languages-wrap d-flex flex-wrap gap-1">
                  {selectedProfile?.details?.crawl_profile_languages?.length >
                  0 ? (
                    selectedProfile?.details?.crawl_profile_languages.map(
                      (item: any, index: any) => {
                        return (
                          <div className="language-item" key={index}>
                            {item.language}
                          </div>
                        );
                      }
                    )
                  ) : (
                    <div className="d-flex w-100 align-items-center justify-content-center">
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-4 col-lg-4">
          <div className="radar-section d-flex justify-content-center">
            <RadarChart datasets={datasets} />
          </div>
          <div className="feedback-section">
            <div
              className={`feedback-overall ${
                calculateConfidence() >= 80
                  ? ""
                  : calculateConfidence() <= 80 && calculateConfidence() >= 50
                  ? "medium"
                  : "bad"
              }`}
            >
              <div className="feedback-title">
                Overall: {parseInt(selectedEvaluate?.overall) / 10}/10
              </div>
              <div className="feedback-description">{feedback?.overall}</div>
            </div>
            <div
              className={`confident-section ${
                calculateConfidence() >= 80
                  ? "good"
                  : calculateConfidence() <= 80 && calculateConfidence() >= 50
                  ? "medium"
                  : "bad"
              }`}
            >
              <div className="confidence-rate">
                Confidence: {calculateConfidence()}%
              </div>
              <div className="confidence-info">
                <Popover
                  content={
                    <PopoverContentAnalysis
                      feedBack={feedback}
                      totalConfidence={calculateConfidence}
                    />
                  }
                  placement="leftBottom"
                  title={`Confidence Analysis`}
                  trigger="click"
                  overlayClassName="Candidate-Popover-Analysis"
                >
                  <InfoCircleOutlined />
                </Popover>
              </div>
              <div className="insufficient-confidence">
                <WarningOutlined className="me-1" />
                Insufficient Confidence
              </div>
            </div>
            <div className="feedback-experience">
              <div className="feedback-title">
                <i className="fa-solid fa-briefcase"></i> Experience:{" "}
                {parseInt(selectedEvaluate?.experience) / 10}/10
              </div>
              <div className="feedback-description">{feedback?.experience}</div>
            </div>
            <div className="feedback-skill">
              <div className="feedback-title">
                <i className="fa-solid fa-lightbulb"></i> Skill:{" "}
                {parseInt(selectedEvaluate?.skills) / 10}/10
              </div>
              <div className="feedback-description">{feedback?.skills}</div>
            </div>
            <div className="feedback-education">
              <div className="feedback-title">
                <i className="fa-solid fa-graduation-cap"></i> Education:{" "}
                {parseInt(selectedEvaluate?.education) / 10}/10
              </div>
              <div className="feedback-description">{feedback?.education}</div>
            </div>
            <div className="feedback-language">
              <div className="feedback-title">
                <i className="fa-solid fa-language"></i> Languages:{" "}
                {parseInt(selectedEvaluate?.languages) / 10}
                /10
              </div>
              <div className="feedback-description">{feedback?.languages}</div>
            </div>
            <div className="feedback-certification">
              <div className="feedback-title">
                <i className="fa-solid fa-award"></i> Certification:{" "}
                {parseInt(selectedEvaluate?.certifications) / 10}
                /10
              </div>
              <div className="feedback-description">
                {feedback?.certifications}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const ProfileItem: React.FC<any> = ({
  item,
  index,
  selectedDetail,
  setSelectedDetail,
  score,
  handleSelectedItem,
  handleExcludedItem,
  positionList,
}) => {
  return (
    <div
      key={index}
      className={`candidate-item px-2 fade-in ${
        item.slug === selectedDetail ? "active" : ""
      } d-flex gap-2 align-items-center`}
      onClick={() => setSelectedDetail(item.slug)}
    >
      <Checkbox
        onChange={(e) =>
          handleSelectedItem(item, e.target.checked, positionList)
        }
        disabled={positionList == "selected" ? true : false}
        checked={
          positionList == "not selected" || positionList == "excluded"
            ? false
            : true
        }
      />
      <div className="flex-shrink-0">
        <img
          src={item?.details?.avatar}
          className="img-fluid rounded-circle"
          width={30}
          alt={item?.detail?.name}
          onError={(e: any) => {
            e.target.onerror = null;
            e.target.src =
              "https://static.vecteezy.com/system/resources/previews/009/292/244/non_2x/default-avatar-icon-of-social-media-user-vector.jpg";
          }}
        />
      </div>
      <div className="flex-grow-1">
        <div className="candidate-name d-flex align-items-center gap-2">
          <span>{item?.details?.name}</span>
          <a href={item?.details?.url} target="_blank" rel="noreferrer">
            <img src={linkedImg} width={15} alt={item?.details?.url} />
          </a>
        </div>
        <div className="candidate-position">{item?.details?.position}</div>
      </div>
      <div className="flex-grow-2 ms-auto">
        <span className="candidate-score me-2">
          {score.length > 0 ? `${parseInt(score[0].overall) / 10}/10` : ""}
        </span>
        <Button
          disabled={positionList == "excluded" ? true : false}
          className={`exclude-btn ${
            positionList == "excluded" ? "excluded" : ""
          }`}
          icon={<MinusCircleOutlined />}
          onClick={() => handleExcludedItem(item, positionList)}
        ></Button>
      </div>
    </div>
  );
};

// POPOVER CONTENT
const PopoverContentState: React.FC<any> = () => (
  <div>
    <div className="state-popover">
      Recruiters evaluate talents based on their{" "}
      <span className="fw-bold underline">
        <u>state</u>
      </span>{" "}
      and determine whether they are:
    </div>
    <div className="state-tutorial mt-3">
      <div className="d-flex align-items-center gap-3">
        <Checkbox className="popover-checkbox" checked={false} />
        <Checkbox className="popover-checkbox" checked={true} />
        <span>Selected from Campaign</span>
      </div>
      <div className="d-flex align-items-center gap-3 mt-2">
        <MinusCircleOutlined className="popover-exlude" />
        <MinusCircleOutlined className="popover-exluded" />
        <span>Excluded from Campaign</span>
      </div>
    </div>
  </div>
);

// POPOVER CONTENT
const PopoverContentAnalysis: React.FC<any> = ({
  feedBack,
  totalConfidence,
}) => (
  <div className="analysis-popover">
    <div className="summories-analyze">
      <div className="progress">
        <div
          className="progress-bar experience"
          style={{
            width: parseFloat(feedBack?.confidence?.experience) * 100 + "%",
          }}
        ></div>
        <div
          className="progress-bar education"
          style={{
            width: parseFloat(feedBack?.confidence?.education) * 100 + "%",
          }}
        ></div>
        <div
          className="progress-bar certification"
          style={{
            width: parseFloat(feedBack?.confidence?.certifications) * 100 + "%",
          }}
        ></div>
        <div
          className="progress-bar skills"
          style={{
            width: parseFloat(feedBack?.confidence?.skills) * 100 + "%",
          }}
        ></div>
        <div
          className="progress-bar languages"
          style={{
            width: parseFloat(feedBack?.confidence?.languages) * 100 + "%",
          }}
        ></div>
      </div>
      <div className="analyze-text">
        Total: <span className="fw-bold">{totalConfidence()}%</span>
      </div>
    </div>
    <div className="analyze-description">
      The candidate's profile provides substantial information about their
      experience and education, but lacks detail about specific skills,
      certifications, and project accomplishments. This limits the confidence in
      making a comprehensive judgment.
    </div>
    <div className="caculate-confidence">
      <div className="title">Calculate Confidence</div>
      <div className="caculate-confidence-item">
        <div className="progress">
          <div
            className="progress-bar experience"
            style={{
              width: parseFloat(feedBack?.confidence?.experience) * 100 + "%",
            }}
          ></div>
        </div>
        <div className="item-description">
          Experience: {parseFloat(feedBack?.confidence.experience) * 100}%
        </div>
        <CheckCircleOutlined style={{ color: "#157F40" }} />
      </div>
      <div className="caculate-confidence-item">
        <div className="progress">
          <div
            className="progress-bar education"
            style={{
              width: parseFloat(feedBack?.confidence?.education) * 100 + "%",
            }}
          ></div>
        </div>
        <div className="item-description">
          Education: {parseFloat(feedBack?.confidence?.education) * 100}%
        </div>
        <CheckCircleOutlined style={{ color: "#157F40" }} />
      </div>
      <div className="caculate-confidence-item">
        <div className="progress">
          <div
            className="progress-bar certification"
            style={{
              width:
                parseFloat(feedBack?.confidence?.certifications) * 100 + "%",
            }}
          ></div>
        </div>
        <div className="item-description">
          Certification: {parseFloat(feedBack?.confidence.certifications) * 100}
          %
        </div>
        <CheckCircleOutlined style={{ color: "#157F40" }} />
      </div>
      <div className="caculate-confidence-item">
        <div className="progress">
          <div
            className="progress-bar skills"
            style={{
              width: parseFloat(feedBack?.confidence.skills) * 100 + "%",
            }}
          ></div>
        </div>
        <div className="item-description">
          Skills: {parseFloat(feedBack?.confidence.skills) * 100}%
        </div>
        <CheckCircleOutlined style={{ color: "#157F40" }} />
      </div>
      <div className="caculate-confidence-item">
        <div className="progress">
          <div
            className="progress-bar languages"
            style={{
              width: parseFloat(feedBack?.confidence.languages) * 100 + "%",
            }}
          ></div>
        </div>
        <div className="item-description">
          Languages: {parseFloat(feedBack?.confidence.languages) * 100}%
        </div>
        <CheckCircleOutlined style={{ color: "#157F40" }} />
      </div>
    </div>
  </div>
);

export default CandidateDetailModal;
